import React, { useState, useContext, useEffect } from 'react';
import { apiUrl } from '../utils/api';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import { UserData } from '../types/UserData';
import { Eye, EyeOff } from 'lucide-react';
import AuthSidebar from './AuthSidebar';
import engazeiqLogo from '../engazeiq_logo.png'; // Import the logo

// --- Styles ---
const LoginContainer = styled.div`
  display: flex;
  height: 100vh;
  width: 100vw;
  background-color: var(--secondary-color);
`;

const LoginMain = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
  background-color:white;
`;

const LoginForm = styled.div`
  width: 100%;
  max-width: 400px;
  border-radius: 12px;
  padding: 32px;
  background-color:white;
  text-align: center;
`;

const LoginHeader = styled.h2`
  font-size: 24px;
  margin-bottom: 24px;
  font-weight: 200;
`;

const InputGroup = styled.div`
  margin-bottom: 20px;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--border-color, #ccc);
  border-radius: 8px;
  font-size: 14px;

  &:focus {
    border-color: var(--accent-color, #FF8000);
    outline: none;
  }
`;

const OptionsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  font-size: 13px;
`;

const RememberMeLabel = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  color: #333;
`;

const ToggleSwitchContainer = styled.label`
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + span {
    background-color: #FF8000;
  }

  &:checked + span:before {
    transform: translateX(12px);
  }
`;

const ToggleSlider = styled.span`
  position: relative;
  display: block;
  width: 24px;
  height: 14px;
  background-color: #ccc;
  border-radius: 14px;
  transition: background-color 0.3s;
  margin-right: 6px;

  &:before {
    content: "";
    position: absolute;
    height: 10px;
    width: 10px;
    left: 2px;
    bottom: 2px;
    background-color: white;
    border-radius: 50%;
    transition: transform 0.3s;
  }
`;

const ToggleLabelText = styled.span`
  font-size: 13px;
  color: #333;
`;

const ForgotLink = styled.a`
  font-size: 14px;
  color:#FF8000;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const LoginButton = styled.button`
  width: 100%;
  padding: 12px;
  background-color: #FF8000;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const RegisterText = styled.div`
  text-align: center;
  margin-top: 20px;
  font-size: 14px;
  color: #666;

  a {
    color: #FF8000;
    text-decoration: none;
    font-weight: 500;
  }
`;

const ErrorMessage = styled.div`
  color: #d32f2f;
  font-size: 14px;
  margin-top: 8px;
  padding: 8px;
  background-color: rgba(211, 47, 47, 0.1);
  border-radius: 4px;
  text-align: center;
`;

const SuccessMessage = styled.div`
  color: #388e3c;
  font-size: 14px;
  margin-top: 8px;
  padding: 8px;
  background-color: rgba(56, 142, 60, 0.1);
  border-radius: 4px;
  text-align: center;
`;

const PasswordWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const ToggleButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  
  &:hover {
    color: #333;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 32px;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
`;

const ModalHeader = styled.h2`
  font-size: 20px;
  margin-bottom: 12px;
  font-weight: 600;
  color: #333;
`;

const ModalDescription = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 20px;
  line-height: 1.5;
`;

const ModalButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  border-radius: 8px;
  border: none;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ModalPrimaryButton = styled(ModalButton)`
  background-color: #FF8000;
  color: white;
  margin-bottom: 12px;

  &:hover:not(:disabled) {
    background-color: #e67e00;
  }
`;

const ModalSecondaryButton = styled(ModalButton)`
  background-color: #f5f5f5;
  color: #333;

  &:hover:not(:disabled) {
    background-color: #e8e8e8;
  }
`;

const ModalButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 24px;
`;

// --- Login Component ---
const Login: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState("");
  const [forgotError, setForgotError] = useState("");
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);

  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  console.log("Google Client ID (Frontend):", googleClientId);

  const handleRegisterClick = () => {
    navigate("/CreateUser");
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError("");
    setForgotMessage("");
    setForgotLoading(true);

    try {
      const res = await fetch(apiUrl("/api/auth/forgot-password"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Failed to send reset email");
      }

      const data = await res.json();
      setForgotMessage(data.message || "Check your email for password reset instructions");
      setForgotEmail("");
      setTimeout(() => {
        setShowForgotPassword(false);
        setForgotMessage("");
      }, 2000);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An unknown error occurred";
      setForgotError(errorMessage);
    } finally {
      setForgotLoading(false);
    }
  };

  const handleGoogleLogin = async (response: any) => {
    console.log("Google ID Token:", response.credential);
    setError("");
    setIsLoading(true);

    try {
      const res = await fetch(apiUrl("/api/auth/google"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token: response.credential, client_id: googleClientId }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || "Google login failed on backend");
      }

      const data = await res.json();
      const userToStore: UserData = {
        ...data.user,
        token: data.access_token,
        preferences: {
          darkMode: false,
          notifications: true,
        },
      };

      if (rememberMe) {
        localStorage.setItem("auth_token", userToStore.token);
        localStorage.setItem("user_data", JSON.stringify(userToStore));
      } else {
        sessionStorage.setItem("auth_token", userToStore.token);
        sessionStorage.setItem("user_data", JSON.stringify(userToStore));
      }

      login(userToStore);
      navigate("/");
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An unknown error occurred";
      setError(`Google login failed: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  // useEffect(() => {
  //   if (typeof window !== 'undefined' && window.google && googleClientId) {
  //     window.google.accounts.id.initialize({
  //       client_id: googleClientId,
  //       callback: handleGoogleLogin,
  //     });
  //     window.google.accounts.id.renderButton(
  //       document.getElementById("google-signin-btn"),
  //       {
  //         theme: "outline",
  //         size: "large",
  //         text: "signin_with",
  //         shape: "rectangular",
  //         logo_alignment: "left",
  //       }
  //     );
  //   }
  // }, [googleClientId, handleGoogleLogin]);

  useEffect(() => {
  if (!googleClientId) return;

  const interval = setInterval(() => {
    if (window.google) {
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: handleGoogleLogin,
      });

      window.google.accounts.id.renderButton(
        document.getElementById("google-signin-btn")!,
        {
          theme: "outline",
          size: "large",
          text: "continue_with",
          shape: "rectangular",
          logo_alignment: "left",
        }
      );

      clearInterval(interval);
    }
  }, 100);

  return () => clearInterval(interval);
}, [googleClientId]);

  const handleLogin = async (e: React.FormEvent) => {
  e.preventDefault();
  setError("");
  setIsLoading(true);

  try {
    const res = await fetch(apiUrl("/api/auth/login"), {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: `username=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`,
    });

      if (!res.ok) {
        if (res.status === 403) {
          const errData = await res.json();
          throw new Error(errData.detail || "Access forbidden");
        }
        throw new Error("Invalid username or password");
      }

    const data = await res.json();

    // ✅ MAP BACKEND RESPONSE → FRONTEND SHAPE
    const userToStore: UserData = {
      ...data.user,                 // id, email, role, company_name
      token: data.access_token,     // 🔥 THIS FIXES EVERYTHING
      preferences: {
        darkMode: false,
        notifications: true,
      },
    };

    if (rememberMe) {
      localStorage.setItem("auth_token", userToStore.token);
      localStorage.setItem("user_data", JSON.stringify(userToStore));
    } else {
      sessionStorage.setItem("auth_token", userToStore.token);
      sessionStorage.setItem("user_data", JSON.stringify(userToStore));
    }

    login(userToStore);
    navigate("/"); // ✅ redirect works now
  } catch (err) {
    const errorMessage =
      err instanceof Error ? err.message : "An unknown error occurred";
    setError(`Login failed: ${errorMessage}`);
  } finally {
    setIsLoading(false);
  }
};

  return (
    <LoginContainer>
      <AuthSidebar />
      <LoginMain>
        <LoginForm>
          <img
            src={engazeiqLogo}
            alt="EngazeIQ Logo"
            style={{ display: "block", height: "50px", margin: "0 auto 20px auto" }}
          />
          <LoginHeader>
            Welcome to <span style={{ fontWeight: 700 }}>
              Engaze<span style={{ color: "#FF6B00" }}>IQ</span>
            </span>
          </LoginHeader>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <form onSubmit={handleLogin}>
            <InputGroup>
              <TextInput
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                name="email"
                autoComplete="username"
                required
              />
            </InputGroup>

            <InputGroup>
              <PasswordWrapper>
                <TextInput
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your password"
                  name="password"
                  autoComplete="current-password"
                  style={{ paddingRight: "40px" }}
                  required
                />
                <ToggleButton
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </ToggleButton>
              </PasswordWrapper>
            </InputGroup>

            <OptionsRow>
              <RememberMeLabel>
                <ToggleSwitchContainer>
                  <ToggleInput
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <ToggleSlider />
                </ToggleSwitchContainer>
                <ToggleLabelText>Remember me</ToggleLabelText>
              </RememberMeLabel>
              <ForgotLink 
                href="#" 
                onClick={(e) => {
                  e.preventDefault();
                  setShowForgotPassword(true);
                }}
              >
                Forgot password?
              </ForgotLink>
            </OptionsRow>

            <LoginButton type="submit" disabled={isLoading}>
              {isLoading ? "Signing in..." : "Sign In"}
            </LoginButton>
            <div id="google-signin-btn" style={{ marginTop: "16px" }} />

          </form>

          <RegisterText>
            Don't have an account?{" "}
            <a href="/CreateUser" onClick={(e) => { e.preventDefault(); handleRegisterClick(); }}>
              Sign up
            </a>
          </RegisterText>

        </LoginForm>
      </LoginMain>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <ModalOverlay onClick={() => !forgotLoading && setShowForgotPassword(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>Reset Password</ModalHeader>
            <ModalDescription>
              Enter your email address and we'll send you a link to reset your password.
            </ModalDescription>

            <form onSubmit={handleForgotPassword}>
              <InputGroup>
                <TextInput
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="Your email address"
                  required
                  disabled={forgotLoading}
                />
              </InputGroup>

              {forgotError && <ErrorMessage>{forgotError}</ErrorMessage>}
              {forgotMessage && <SuccessMessage>{forgotMessage}</SuccessMessage>}

              <ModalButtonGroup>
                <ModalPrimaryButton type="submit" disabled={forgotLoading || !forgotEmail.trim()}>
                  {forgotLoading ? "Sending..." : "Send Reset Link"}
                </ModalPrimaryButton>
                <ModalSecondaryButton
                  type="button"
                  onClick={() => setShowForgotPassword(false)}
                  disabled={forgotLoading}
                >
                  Cancel
                </ModalSecondaryButton>
              </ModalButtonGroup>
            </form>
          </ModalContent>
        </ModalOverlay>
      )}
    </LoginContainer>
  );
};

export default Login;
